import { useNavigation } from '@react-navigation/native'
import * as Clipboard from 'expo-clipboard'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FlatList,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native'
import { Icon, Text } from 'react-native-paper'
import { Menu, MenuSection } from '../../../../../@ui'
import Toast from 'react-native-root-toast'

import { Cluster } from '../types'
import { Article } from '../../../perspectives/perspective/types'
import { UserLink } from '../../../../../@ui/typo/user.link'
import { PerspectiveTile } from './perspective.tile'
import { ClusterHeaderButton } from './cluster-header-button'
import {
  ContributionType,
  useArticlesForClusterQuery,
} from '../../../../../generated/graphql'
import { AppNavigation } from '../../../../../navigation/types'
import { useAuthContext } from '../../../../../contexts/auth-context'
import { SocketEvents, useSocketRoom } from '../../../../../socket/socket.hooks'
import { Env } from '../../../../../env'
import {
  IconAdd,
  IconClusterSettings,
  IconFilter,
  IconImport,
  IconPrivate,
} from '../../../../../utils/meldd-icons'
import { importFile } from '../../../../../utils/import-utils'
import {
  MelddCard,
  MelddSearchBar,
  PaginatedFlatList,
  Row,
} from '../../../../../@ui'
import { TutorialWrapper as TutorialComponent } from '../../../../../@ui/containers/others/tutorial/tutorial.wrapper'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import { useTheme } from '../../../../../hooks/theme.hook'
import { TouchableIconWithToolTip } from '../../../../../utils/meldd-tooltip'

interface ClusterCardProps {
  cluster: Cluster
  onRefresh: () => void
  refreshing: boolean
  onPerspectivePress: (perspective: Article) => void
  navigateToContributions: (
    perspectiveId: string,
    contributionType: ContributionType
  ) => void
}

const NoPerspectivesFound = ({
  cluster,
  searchInput,
  createArticle,
}: {
  cluster: Cluster
  searchInput: string
  createArticle: () => void
}) => {
  const { t } = useTranslation('clusters')
  const navigation = useNavigation<AppNavigation>()
  const theme = useTheme()
  return (
    <View
      style={{
        marginTop: theme.spacing(8),
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Text style={{ textAlign: 'center', fontSize: 12 }}>
        {t('no-perspectives-found')}{' '}
        <TouchableOpacity onPress={() => createArticle()}>
          <Text style={{ textDecorationLine: 'underline' }}>
            {t('create-a-perspective')}
          </Text>
        </TouchableOpacity>
        {searchInput && (
          <>
            {' '}
            <Text style={{}}>{t('or')}</Text>{' '}
            <TouchableOpacity
              onPress={() =>
                navigation.navigate('Search', {
                  nodeId: cluster.nodeId,
                  searchQuery: searchInput,
                })
              }
            >
              <Text style={{ textDecorationLine: 'underline' }}>
                {t('make-a-global-search')}
              </Text>
            </TouchableOpacity>
          </>
        )}
      </Text>
    </View>
  )
}

interface ClusterCardMenuProps {
  cluster: Cluster
  onFilterToggle: () => void
  displaySearchBar: boolean
}

function ClusterCardMenu({
  cluster,
  onFilterToggle,
  displaySearchBar,
}: ClusterCardMenuProps) {
  const { t } = useTranslation('clusters')
  const navigation = useNavigation<AppNavigation>()
  const [menuVisible, setMenuVisible] = useState(false)
  const theme = useTheme()

  const saveClusterUrl = async () => {
    const urlToClipBoard = `${Env.WEB_APP_URL}/ClusterCards?clusterId=${cluster.id}&nodeId=${cluster.nodeId}`
    Toast.show('URL copied!', { position: Toast.positions.TOP })
    await Clipboard.setStringAsync(urlToClipBoard)
  }

  const menuButtonStyle = {
    justifyContent: 'center' as const,
    backgroundColor: 'transparent' as const,
  }

  const sections: MenuSection[] = [
    {
      label: 'Cluster',
      items: [
        {
          title: t('copy-link'),
          icon: 'content-copy',
          onPress: saveClusterUrl,
        },
        ...(cluster.isModerator
          ? [
              {
                title: t('Settings'),
                icon: IconClusterSettings,
                onPress: () => {
                  navigation.navigate('ClusterSettings', {
                    clusterId: cluster.id,
                  })
                },
              },
            ]
          : []),
      ],
    },
    {
      label: t('perspectives'),
      items: [
        {
          title: t('search-clusters'),
          icon: displaySearchBar ? 'filter' : 'filter-outline',
          onPress: onFilterToggle,
        },
      ],
    },
  ]

  return (
    <>
      <Menu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <TouchableIconWithToolTip
            icon={
              <Icon
                source="menu"
                size={28}
                color={theme.colors.onSurfaceVariant}
              />
            }
            tooltip={t('menu')}
            mode="text"
            onPress={() => setMenuVisible(true)}
          />
        }
        sections={sections}
        anchorPosition="bottom"
      />
    </>
  )
}

export function ClusterCard(props: ClusterCardProps) {
  const { width } = useWindowDimensions()
  const { t } = useTranslation('clusters')
  const theme = useTheme()
  const { authAction } = useAuthContext()
  const navigation = useNavigation<AppNavigation>()
  const { cluster, refreshing } = props
  const styles = useStyle()
  const [searchInput, setSearchInput] = useState('')
  const [displaySearchBar, setDisplaySearchBar] = useState(false)
  const listRef = useRef<FlatList<Article>>(null)
  const [scrollToId, setScrollToId] = useState<string | null>(null)

  const {
    data: articlesForCluster,
    fetchMore,
    refetch,
  } = useArticlesForClusterQuery({
    variables: {
      input: {
        clusterId: cluster.id,
        limit: 15,
        offset: 0,
      },
    },
  })

  const socketCallback = useCallback(() => {
    refetch()
  }, [refetch])

  useSocketRoom(cluster.id).event(SocketEvents.Refresh, socketCallback)

  const createArticle = () => {
    authAction(navigation, () =>
      navigation.navigate('CreateArticle', {
        clusterId: cluster.id,
      })
    )
  }

  const handleImport = () => {
    authAction(navigation, () => importFile(navigation, cluster.id))
  }

  const articles = articlesForCluster?.articlesForCluster?.results ?? []

  const filteredArticles =
    searchInput.trim().length === 0
      ? articles
      : articles.filter((article) =>
          article.title
            .toLocaleLowerCase()
            .includes(searchInput.trim().toLocaleLowerCase())
        )

  //Scoll to after data is updated
  useEffect(() => {
    if (!scrollToId) {
      return
    }

    const newIndex = articles.findIndex((a) => a.id === scrollToId)
    if (newIndex !== -1) {
      listRef.current?.scrollToIndex({ index: newIndex, animated: true })
    }
    setScrollToId(null) // reset after scrolling
  }, [articles, scrollToId])

  return (
    <MelddCard
      header={
        <View style={styles.titleWrapper}>
          <View style={styles.title}>
            {/* First row: Title and Menu */}
            <Row justifyContent="space-between" columnGap={2}>
              <View style={{ flex: 1 }}>
                <Text
                  variant={width < 480 ? 'titleMedium' : 'titleLarge'}
                  style={styles.titleText}
                >
                  {cluster.topic}
                </Text>
                {/* Second row: Private/Public */}
                <View style={[styles.subTitle]}>
                  <Icon
                    source={cluster.isPrivate ? 'earth-off' : 'earth'}
                    size={10}
                    color={theme.colors.onSurfaceVariant}
                  />
                  <Text
                    variant="labelSmall"
                    style={{ color: theme.colors.onSurfaceVariant }}
                  >
                    {cluster.isPrivate ? t('Private') : t('Public')}
                  </Text>
                </View>
              </View>
              <ClusterCardMenu
                cluster={cluster}
                onFilterToggle={() => setDisplaySearchBar(!displaySearchBar)}
                displaySearchBar={displaySearchBar}
              />
            </Row>

            {/* Fourth row: Filter (if enabled) */}
            {displaySearchBar && (
              <Row style={{ marginTop: theme.spacing(2) }}>
                <MelddSearchBar
                  icon={IconFilter}
                  placeholder={t('search-clusters')}
                  onChangeText={setSearchInput}
                  value={searchInput}
                  autoFocus={true}
                  style={{ button: { backgroundColor: 'transparent' } }}
                />
              </Row>
            )}

            {cluster.moderators.length > 0 && (
              <View style={styles.moderators}>
                <Text variant={'bodySmall'}>{t('Moderators')}:</Text>
                {cluster.moderators.map((moderator, index) => (
                  <UserLink key={index} user={moderator.user} />
                ))}
              </View>
            )}
          </View>
        </View>
      }
    >
      {/* Third row: Import and Perspective buttons */}
      <Row
        columnGap={4}
        style={{ margin: theme.spacing(4) }}
        justifyContent="space-between"
      >
        <View style={{ flex: 1, maxWidth: 300 }}>
          <ClusterHeaderButton
            icon={IconImport}
            onPress={handleImport}
            variant="secondary"
          >
            {t('Import')}
          </ClusterHeaderButton>
        </View>
        <View style={{ flex: 1, maxWidth: 300 }}>
          <TutorialComponent tutorialId="create_perspective" offsetX={28}>
            <ClusterHeaderButton icon={IconAdd} onPress={() => createArticle()}>
              {t('Perspective')}
            </ClusterHeaderButton>
          </TutorialComponent>
        </View>
      </Row>
      <PaginatedFlatList
        ref={listRef}
        data={filteredArticles as Article[]}
        total={
          searchInput.trim().length === 0
            ? articlesForCluster?.articlesForCluster?.total ?? 0
            : filteredArticles.length
        }
        onLoadMore={async (params) => {
          await fetchMore({
            variables: {
              input: {
                clusterId: cluster.id,
                ...params,
                limit: 15,
              },
            },
          })
        }}
        onRefresh={async () => {
          await refetch()
        }}
        renderItem={({ item }: { item: Article }) => (
          <PerspectiveTile
            cluster={cluster}
            perspective={item}
            onVoteChange={() => {
              // Scroll after the page has refetch
              setTimeout(() => {
                setScrollToId(item.id)
              }, 100)
            }}
            onPress={() => props.onPerspectivePress(item)}
            navigateToContributions={(contributionType) =>
              props.navigateToContributions?.(item.id, contributionType)
            }
          />
        )}
        extraData={cluster.articleVotes}
        emptyComponent={
          <NoPerspectivesFound
            cluster={cluster}
            searchInput={searchInput}
            createArticle={() => createArticle()}
          />
        }
        refreshing={refreshing}
      />
    </MelddCard>
  )
}

const useStyle = createStyle(({ theme }) => ({
  titleWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    paddingHorizontal: theme.spacing(4),
    minHeight: 100,
  },
  title: {
    flex: 1,
  },
  mainTitle: {
    flexDirection: 'row',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleText: {
    flex: 1,
  },
  subTitle: {
    color: theme.colors.onSurfaceVariant,
    flexDirection: 'row',
    alignItems: 'center',
  },
  moderators: {
    flexDirection: 'row',
    columnGap: theme.spacing(2),
    marginTop: theme.spacing(1),
    overflow: 'hidden',
  },
  moderator: {
    flexShrink: 1,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
    paddingVertical: theme.spacing(3),
    paddingHorizontal: theme.spacing(4),
  },
}))
